import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Copy, ArrowsClockwise } from "@phosphor-icons/react";

export default function History() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tests?limit=500");
      setTests(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const filtered = tests.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      t.username?.toLowerCase().includes(s) ||
      t.client_name?.toLowerCase().includes(s) ||
      t.phone_number?.toLowerCase().includes(s)
    );
  });

  return (
    <Layout
      title="Histórico de testes"
      subtitle="Operação / Logs"
      action={
        <button
          data-testid="history-refresh-btn"
          onClick={load}
          className="flex items-center gap-2 border border-[#E5E7EB] px-4 py-2 text-sm hover:bg-[#F8F9FA] transition-colors duration-150"
        >
          <ArrowsClockwise size={16} weight="bold" /> Atualizar
        </button>
      }
    >
      <div className="border border-[#E5E7EB] bg-white">
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <input
            data-testid="history-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por usuário, nome ou telefone…"
            className="w-full max-w-sm bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7]"
          />
          <div className="label-mono">{filtered.length} resultados</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F8F9FA]">
                <Th>Data/Hora</Th>
                <Th>Cliente</Th>
                <Th>Usuário</Th>
                <Th>Senha</Th>
                <Th>Telefone</Th>
                <Th>Origem</Th>
                <Th>Modo</Th>
                <Th>Enviado</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-[#64748B]">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-[#64748B]">
                    Nenhum teste encontrado.
                  </td>
                </tr>
              )}
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  data-testid={`history-row-${t.id}`}
                  className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA] transition-colors duration-150"
                >
                  <Td>{new Date(t.created_at).toLocaleString("pt-BR")}</Td>
                  <Td>{t.client_name}</Td>
                  <Td className="mono">{t.username}</Td>
                  <Td className="mono">{t.password}</Td>
                  <Td className="mono">{t.phone_number || "—"}</Td>
                  <Td>
                    <span
                      className={`label-mono px-2 py-0.5 ${
                        t.source === "whatsapp"
                          ? "bg-[#10B981]/10 text-[#10B981]"
                          : "bg-[#002FA7]/10 text-[#002FA7]"
                      }`}
                    >
                      {t.source}
                    </span>
                  </Td>
                  <Td className="label-mono">{t.mode}</Td>
                  <Td>
                    <span className={t.sent ? "text-[#10B981]" : "text-[#64748B]"}>
                      {t.sent ? "Sim" : "—"}
                    </span>
                  </Td>
                  <Td>
                    <button
                      data-testid={`copy-creds-${t.id}`}
                      onClick={() => copy(`Usuário: ${t.username}\nSenha: ${t.password}`)}
                      className="text-[#002FA7] hover:underline flex items-center gap-1 text-xs"
                    >
                      <Copy size={14} weight="bold" /> Copiar
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-4 py-2.5 label-mono font-medium">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>
);
